import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Partner, CreatePartnerData, UpdatePartnerData } from '@/services/partnerService';

interface AddEditPartnerDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreatePartnerData | UpdatePartnerData) => Promise<void>;
  partner?: Partner | null;
  loading?: boolean;
}

export const AddEditPartnerDialog: React.FC<AddEditPartnerDialogProps> = ({
  open,
  onClose,
  onSave,
  partner,
  loading = false,
}) => {
  const [formData, setFormData] = useState<CreatePartnerData>({
    name: '',
    email: '',
    phone: '',
    type: 'Garage & Inspection',
    serviceType: 'Inspection',
    address: '',
    city: '',
    state: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string>('');

  useEffect(() => {
    if (partner) {
      setFormData({
        name: partner.name,
        email: partner.email || '',
        phone: partner.phone || '',
        type: partner.type,
        serviceType: partner.serviceType,
        address: partner.address || '',
        city: partner.city || '',
        state: partner.state || '',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        type: 'Garage & Inspection',
        serviceType: 'Inspection',
        address: '',
        city: '',
        state: '',
      });
    }
    setErrors({});
    setSaveError('');
  }, [partner, open]);

  const handleChange = (field: keyof CreatePartnerData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTypeChange = (type: string) => {
    let serviceType: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance' = 'Inspection';

    if (type === 'GPS Installation') {
      serviceType = 'GPS';
    } else if (type === 'Insurance Verification') {
      serviceType = 'Insurance';
    } else if (type === 'Documentation') {
      serviceType = 'Documentation';
    }

    setFormData(prev => ({
      ...prev,
      type: type as any,
      serviceType,
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Partner name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {

    if (!validate()) {
      return;
    }

    setSaveError('');

    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('❌ Save error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save partner';
      setSaveError(errorMessage);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {partner ? 'Edit Partner' : 'Add New Partner'}
      </DialogTitle>

      <DialogContent>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Partner Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Partner Type</InputLabel>
              <Select
                value={formData.type}
                label="Partner Type"
                onChange={(e) => handleTypeChange(e.target.value)}
              >
                <MenuItem value="Garage & Inspection">Garage & Inspection</MenuItem>
                <MenuItem value="GPS Installation">GPS Installation</MenuItem>
                <MenuItem value="Insurance Verification">Insurance Verification</MenuItem>
                <MenuItem value="Documentation">Documentation</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              error={!!errors.phone}
              helperText={errors.phone}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="State"
              value={formData.state}
              onChange={(e) => handleChange('state', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : partner ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
